import { colors } from './colors';

// Custom Integral Analytics theme for Kepler.gl
// This theme follows Kepler.gl's theming structure while applying our brand colors
export const integralAnalyticsTheme = {
  // Theme identification
  themeName: 'integralAnalytics',
  // Main Colors
  primaryColor: colors.primaryDeepBlue, // Changed from primaryBrightBlue
  secondaryColor: colors.primaryBrightBlue, // Changed from secondaryRoyalBlue

  // Backgrounds
  sidePanelBg: colors.bgDeepGray,
  titleTextColor: colors.grayLight,
  sidePanelHeaderBg: colors.bgDarkerGray,
  panelBackground: colors.bgDarkerGray,
  panelBackgroundHover: colors.grayCharcoal,
  panelActiveBg: colors.primaryLightBlue, // Changed from grayLight for a touch of blue

  // Interactive Elements
  subtextColorActive: colors.primaryBrightBlue,
  tooltipBg: colors.primaryDeepBlue,
  tooltipColor: colors.grayLight,
  dropdownListBgd: colors.bgDarkerGray,
  textColorHl: colors.grayLight, //primaryDarkBlue

  // Input Fields
  inputBgd: colors.bgDarkerGray,
  inputBgdHover: colors.grayCharcoal,
  inputBgdActive: colors.grayCharcoal,
  dropdownListHighlightBg: colors.primaryMediumBlue, // Changed from grayMediumDark

  // Secondary Elements
  secondaryInputBgd: colors.bgDarkerGray,
  secondaryInputBgdActive: colors.bgDarkerGray,
  secondaryInputBgdHover: colors.grayCharcoal,

  // Additional Theme Properties
  activeColor: colors.primaryBrightBlue,
  textColor: colors.grayLight,
  labelColor: colors.coolGrayLight,
  mapPanelBackgroundColor: colors.bgDeepGray,
  mapPanelHeaderBackgroundColor: colors.bgDarkerGray,

  // Button Colors
  buttonBgd: colors.primaryBrightBlue,
  buttonColor: colors.bgLight, // Pure white for best contrast
  buttonFontWeight: 'bold', // Make button text bold for better readability
  buttonBgdHover: colors.primaryDeepBlue,
  buttonBgdActive: colors.primaryDeepBlue,

  // Accent Colors - Let's use a blue-gray for a more subtle accent
  accentColor: colors.blueGrayMid, // Changed from yellowBright
  highlightColor: colors.primaryLightBlue, // Changed from yellowBright

  // Chart Colors
  chartColors: [
    colors.primaryDeepBlue,
    colors.primaryBrightBlue,
    colors.secondaryMidBlue,
    colors.blueGrayMid, // Changed from blueGrayDark
    colors.primaryMediumBlue, // Changed from primaryDeepBlue (already used)
    colors.secondaryRoyalBlue, // Changed from secondaryNavyBlue
    colors.blueGrayLight, // Changed from yellowMustard
    colors.grayMid, // Changed from grayDark
  ],

  // Extended Kepler.gl theme properties for compatibility
  panelContentBackground: colors.bgDarkerGray,
  subtextColor: colors.coolGrayLight,
  activeColorHover: colors.primaryMediumBlue,
  // Selection highlight (improved contrast)
  selectColor: colors.secondaryRoyalBlue, // darker blue for higher contrast against light text
  selectColorLT: colors.secondaryRoyalBlue,
  selectTextColor: colors.grayLight,

  inputBorderColor: colors.grayCharcoal,
  inputBorderActiveColor: colors.primaryBrightBlue,
  inputColor: colors.grayLight,

  dropdownListBorderTop: `1px solid ${colors.grayCharcoal}`,
  dropdownListShadow: '0 6px 12px 0 rgba(0, 0, 0, 0.16)',

  // Primary buttons
  primaryBtnBgd: colors.primaryBrightBlue,
  primaryBtnActBgd: colors.primaryDeepBlue,
  primaryBtnColor: colors.bgLight,
  primaryBtnActColor: colors.bgLight,
  primaryBtnBgdHover: colors.primaryMediumBlue,

  // Secondary buttons
  secondaryBtnBgd: colors.bgDarkerGray,
  secondaryBtnActBgd: colors.primaryLightBlue,
  secondaryBtnColor: colors.grayLight,
  secondaryBtnActColor: colors.primaryDeepBlue,
  secondaryBtnBgdHover: colors.grayCharcoal,

  // Negative/danger buttons
  negativeBtnBgd: colors.errorRed,
  negativeBtnActBgd: colors.errorRedDark,
  negativeBtnBgdHover: colors.errorRedHover,

  // Links
  linkBtnBgd: 'transparent',
  linkBtnColor: colors.primaryBrightBlue,
  linkBtnActColor: colors.primaryDeepBlue,
  linkBtnActBgd: colors.primaryLightBlue,

  // Modal and overlays
  modalBgd: colors.bgDarkerGray,
  modalBgdHover: colors.grayCharcoal,
  modalHeaderBgd: colors.bgDeepGray,
  modalHeaderColor: colors.grayLight,
  modalFooterBgd: colors.bgDeepGray,
  modalContentBgd: colors.bgDarkerGray,

  // Border colors
  borderColor: colors.grayCharcoal,
  borderColorLT: colors.grayMid,

  // Special brand accent colors
  brandPrimary: colors.primaryBrightBlue,
  brandSecondary: colors.secondaryRoyalBlue,
  brandAccent: colors.blueGrayMid,

  // Loading and progress
  loadingBg: colors.primaryLightBlue,

  // Error states
  errorColor: colors.errorRed,

  // Success states
  successColor: colors.successGreen,

  // Warning states
  warningColor: colors.yellowMustard,

  // Switch and checkbox controls
  switchTrackBgd: colors.grayCharcoal,
  switchTrackBgdActive: colors.primaryBrightBlue,
  switchBtnBgd: colors.grayLight,
  switchBtnBgdActive: colors.grayLight,

  // Chicklets and tags
  chickletBgd: colors.primaryBrightBlue,
  chickletBgdHover: colors.primaryMediumBlue,
  chickletColor: colors.primaryDeepBlue,

  // Range slider
  sliderBarColor: colors.grayCharcoal,
  sliderBarBgd: colors.bgDeepGray,
  sliderHandleColor: colors.primaryBrightBlue,
  sliderHandleHoverColor: colors.primaryDeepBlue,

  // Histogram and charts
  histogramFillInRange: colors.primaryBrightBlue,
  histogramFillOutRange: colors.grayCharcoal,

  // Logo positioning (custom properties for our implementation)
  logoBackground: colors.bgDarkerGray,
  logoBackgroundCollapsed: 'transparent',
  logoTextColor: colors.grayLight,

  // SQL Panel specific styling
  panelHeaderBackground: colors.bgDarkerGray,
  sqlPanelBackground: colors.bgDeepGray,
  sqlPanelBorderColor: colors.grayCharcoal,

  // Error styling
  errorBackground: colors.errorRed + '1A', // 10% opacity (1A in hex = 26/255 ≈ 10%)

  // CodeMirror editor styling
  editorBackground: '#1e1e1e', // Dark background for code editor
  editorTextColor: colors.grayLight,
  editorSelection: colors.primaryMediumBlue,
  editorCursor: colors.grayLight,
  editorLineNumber: colors.coolGrayLight,
  editorActiveLine: colors.grayCharcoal,

  // SQL Panel resize handle
  resizeHandleColor: colors.grayCharcoal,
  resizeHandleHoverColor: colors.grayMid,

  // Schema panel styling
  schemaPanelBackground: colors.bgDarkerGray,
  schemaTreeNodeColor: colors.grayLight,
  schemaTreeNodeHover: colors.grayCharcoal,

  // Data table styling
  dataTableHeaderBg: colors.bgDarkerGray,
  dataTableCellBorder: colors.grayCharcoal,
  dataTableRowHover: colors.grayCharcoal,

  // Data table and field token styling (required by SQL panel and PreviewDataPanel)
  // Field token sizing (datatype pills in dropdowns / tables)
  // Narrowed width & padding to make datatype boxes leaner in layer panel selectors
  fieldTokenWidth: 68, // was 120 (kepler default 120) – reduce horizontal blank space
  fieldTokenHeight: 22, // slightly shorter (was 24) for a tighter look
  fieldTokenPadding: '0 6px', // was '0 8px'
  fieldTokenFontSize: 12,
  fieldTokenFontWeight: 500,
  fieldTokenBorderRadius: 2,
  fieldTokenBg: colors.bgDarkerGray,
  fieldTokenColor: colors.grayLight,
  fieldTokenBorder: `1px solid ${colors.grayCharcoal}`,
  fieldTokenRightMargin: 4, // default 4

  // Accessibility and High Contrast Support
  // Modern contrast ratios meeting WCAG 2.1 AA standards (4.5:1 for normal text, 3:1 for large text)
  highContrastText: colors.bgLight, // Pure white for maximum contrast on dark backgrounds
  highContrastBackground: colors.bgDark, // Pure black for maximum contrast
  highContrastBorder: colors.bgLight, // White borders for high contrast mode
  highContrastFocus: '#66b3ff', // Bright blue for focus indicators

  // Focus indicators for keyboard navigation (WCAG 2.1 compliant)
  focusOutlineColor: colors.primaryBrightBlue,
  focusOutlineWidth: '2px',
  focusOutlineStyle: 'solid',
  focusOutlineOffset: '2px',

  // Enhanced contrast ratios for critical UI elements
  criticalTextColor: colors.bgLight, // Ensures 21:1 contrast ratio on dark backgrounds
  criticalBackgroundColor: colors.bgDeepGray, // Dark background for critical elements
  criticalBorderColor: colors.primaryBrightBlue, // Bright blue for important borders

  // Accessible color combinations
  accessiblePrimary: '#339AF0', // Meets WCAG AA contrast requirements
  accessibleSecondary: '#66b3ff', // High contrast blue
  accessibleSuccess: '#00C851', // High contrast green
  accessibleWarning: '#FFB627', // High contrast yellow
  accessibleError: '#FF5252', // High contrast red
  accessibleInfo: '#33B5E5', // High contrast cyan

  // System color support for forced-colors mode
  systemCanvasText: 'CanvasText',
  systemCanvas: 'Canvas',
  systemFieldText: 'FieldText',
  systemField: 'Field',
  systemButtonText: 'ButtonText',
  systemButtonFace: 'ButtonFace',
  systemHighlight: 'Highlight',
  systemHighlightText: 'HighlightText',
  systemLinkText: 'LinkText',
  systemGrayText: 'GrayText',

  // Motion and animation preferences
  respectsReducedMotion: true, // Flag to check for prefers-reduced-motion
  reducedMotionDuration: '0.01ms', // Minimal duration for reduced motion
  normalAnimationDuration: '0.3s', // Standard animation duration

  // Typography accessibility
  minimumFontSize: 14, // Minimum readable font size
  fontFamilyMono: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace",
  lineHeightMultiplier: 1.5, // WCAG recommended line height
  headingFontWeight: 'bold', // Strong font weight for headings
  bodyFontWeight: 'normal', // Normal weight for body text

  // Interactive element sizing (meets touch target requirements)
  minimumTouchTarget: 44, // 44px minimum touch target size (WCAG guideline)
  buttonMinHeight: 44, // Minimum button height for accessibility
  buttonMinWidth: 44, // Minimum button width for accessibility

  // Skip link styling for keyboard navigation
  skipLinkBackground: colors.bgDark,
  skipLinkColor: colors.bgLight,
  skipLinkPadding: '8px 16px',
  skipLinkBorderRadius: '4px',
  skipLinkZIndex: 10000,

  // Screen reader and assistive technology support
  screenReaderOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  },

  // File type badge colors (used in Azure provider browser UI)
  badgeCsvBg: colors.primaryBrightBlue,
  badgeParquetBg: '#8957e5', // distinct purple for parquet
  badgeJsonBg: '#9e6a03',
  badgeGeoBg: '#238636',
  badgeFileBg: '#57606a',
};
